// First Mile picklist drill-down — the pick legs behind a clicked 30-min-SLA figure, optionally
// narrowed to one warehouse. Rows come back worst-first (longest wait / slowest pick).
import { Drawer, Pagination, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import {
  FirstMilePicklistEscalationBucketKey,
  FirstMilePicklistEscalationRow,
  PicklistEscalationDrilldownKey,
  listFirstMilePicklistEscalationRows,
} from "../control_tower.api";
import {
  FIRST_MILE_DOCK_LABEL,
  PICKLIST_BUCKET_LABEL,
  PICKLIST_DRILLDOWN_LABEL,
} from "../first_mile_meta";
import { FirstMileWarehouseKey } from "../types";

const BUCKET_COLOR: Record<FirstMilePicklistEscalationBucketKey, string> = {
  pending_over_30m: "volcano",
  slow_pick_over_30m: "orange",
};

export interface PicklistDrilldownRequest {
  bucket: PicklistEscalationDrilldownKey;
  dock?: FirstMileWarehouseKey;
}

interface Props {
  request: PicklistDrilldownRequest | null; // null = closed
  dispatchFrom: string;
  dispatchTo: string;
  assignedTo?: string; // same owner filter the clicked figure was scoped by, so counts agree
  onClose: () => void;
}

const dash = <span style={{ color: "#bbb" }}>—</span>;

// 47 -> "47m"; 135 -> "2h 15m".
const fmtMinutes = (m: number | null) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const columns: ColumnsType<FirstMilePicklistEscalationRow> = [
  { title: "PO Number", dataIndex: "po_number", key: "po_number", render: (v) => v ?? dash },
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: (v) => v ?? dash },
  { title: "Vendor", dataIndex: "vendor", key: "vendor", render: (v) => v ?? dash },
  {
    title: "Warehouse",
    dataIndex: "dock",
    key: "dock",
    render: (v: FirstMileWarehouseKey) => FIRST_MILE_DOCK_LABEL[v] ?? v,
  },
  {
    // The wait (pending) or the pick duration (slow) — the number the bucket is about.
    title: "Elapsed",
    dataIndex: "minutes",
    key: "minutes",
    render: (m: number | null) => <strong>{fmtMinutes(m)}</strong>,
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
    render: (r: FirstMilePicklistEscalationBucketKey) => (
      <Tag color={BUCKET_COLOR[r]}>{PICKLIST_BUCKET_LABEL[r]}</Tag>
    ),
  },
];

const titleFor = (req: PicklistDrilldownRequest) => {
  const base = PICKLIST_DRILLDOWN_LABEL[req.bucket] ?? req.bucket;
  return req.dock ? `${FIRST_MILE_DOCK_LABEL[req.dock]} · ${base}` : base;
};

const FirstMilePicklistEscalationDrawer: React.FC<Props> = ({
  request,
  dispatchFrom,
  dispatchTo,
  assignedTo,
  onClose,
}) => {
  const [rows, setRows] = useState<FirstMilePicklistEscalationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [request]);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    listFirstMilePicklistEscalationRows({
      bucket: request.bucket,
      dock: request.dock,
      dispatch_from: dispatchFrom,
      dispatch_to: dispatchTo,
      assigned_to: assignedTo,
      page,
      page_size: pageSize,
    }).then((res) => {
      if (cancelled) return;
      setRows(res?.results ?? []);
      setTotal(res?.count ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [request, dispatchFrom, dispatchTo, assignedTo, page, pageSize]);

  return (
    <Drawer
      title={request ? titleFor(request) : ""}
      width={900}
      open={!!request}
      onClose={onClose}
      destroyOnClose
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 820 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          showTotal={(t) => `${t} pick${t === 1 ? "" : "s"}`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>
    </Drawer>
  );
};

export default FirstMilePicklistEscalationDrawer;
